import {
  CanAccess,
  type Identifier,
  RecordContextProvider,
  required,
  useCanAccess,
  useCreatePath,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { Create } from "@/components/admin/create";
import { CreateButton } from "@/components/admin/create-button";
import { DateField } from "@/components/admin/date-field";
import { DateInput } from "@/components/admin/date-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { Edit } from "@/components/admin/edit";
import { EditButton } from "@/components/admin/edit-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { RecordField } from "@/components/admin/record-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectField } from "@/components/admin/select-field";
import { Show } from "@/components/admin/show";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextField } from "@/components/admin/text-field";
import { TextInput } from "@/components/admin/text-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";

import { contactOptionText } from "../misc/ContactOption";
import type { CrmDataProvider } from "../providers/types";
import { InvoiceCard } from "./invoices";
import { ContractPanel } from "./ContractPanel";
import { IthnkPage } from "./IthnkPage";
import {
  type Delivery,
  deliveryPhase,
  deliveryPhaseChoices,
  type Invoice,
  type Milestone,
} from "./ithnkUtils";
import { StatusBoard } from "./StatusBoard";

const DeliveryListActions = () => {
  const { identity } = useGetIdentity();
  return (
    <div className="flex items-center gap-2">
      {identity ? (
        <ToggleFilterButton
          className="w-auto"
          label="crm.common.me"
          value={{ assigned_sales_id: identity.id }}
        />
      ) : null}
      <ExportButton />
      <CanAccess action="create">
        <CreateButton />
      </CanAccess>
    </div>
  );
};

export const PhaseField = () => (
  <SelectField source="phase" choices={deliveryPhaseChoices} />
);

/** All / Not started / In progress / Completed */
const PhaseTabs = () => {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const { phase, ...otherFilters } = filterValues;
  return (
    <Tabs
      value={phase ?? "all"}
      onValueChange={(value) =>
        setFilters(
          value === "all" ? otherFilters : { ...otherFilters, phase: value },
        )
      }
    >
      <TabsList>
        <TabsTrigger value="all">
          {translate("resources.deliveries.all")}
        </TabsTrigger>
        {deliveryPhaseChoices.map((choice) => (
          <TabsTrigger key={choice.id} value={choice.id}>
            {translate(choice.name)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};

/** A delivery as a card: title, company and deal, phase and current milestone */
const DeliveryCard = ({ milestone }: { milestone?: string }) => {
  const record = useRecordContext<Delivery>();
  if (!record) return null;
  return (
    <Card className="py-3 shadow-sm hover:shadow-md">
      <CardContent className="px-3 flex flex-col gap-1 text-sm">
        <span className="font-medium">{record.title}</span>
        <span className="text-xs text-muted-foreground">
          <ReferenceField
            source="company_id"
            reference="companies"
            link={false}
          />
          {record.deal_id ? (
            <>
              {" - "}
              <ReferenceField source="deal_id" reference="deals" link={false}>
                <TextField source="name" />
              </ReferenceField>
            </>
          ) : null}
        </span>
        <span className="flex items-center gap-2 text-xs">
          <Badge variant="outline">
            <PhaseField />
          </Badge>
          {milestone}
        </span>
      </CardContent>
    </Card>
  );
};

const DeliveryCards = () => {
  const { data = [] } = useListContext<Delivery>();
  const createPath = useCreatePath();
  // the current milestone names of the whole page in one request
  const { data: milestones = [] } = useGetList<Milestone>(
    "delivery_milestones",
    {
      filter: { "delivery_id@in": `(${data.map((d) => d.id).join(",")})` },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "position", order: "ASC" },
    },
    { enabled: data.length > 0 },
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2">
      {data.map((delivery) => (
        <RecordContextProvider key={delivery.id} value={delivery}>
          <Link
            to={createPath({
              resource: "deliveries",
              id: delivery.id,
              type: "show",
            })}
          >
            <DeliveryCard
              milestone={
                milestones.find(
                  (m) =>
                    m.delivery_id === delivery.id &&
                    m.position === delivery.current_position,
                )?.name
              }
            />
          </Link>
        </RecordContextProvider>
      ))}
    </div>
  );
};

export const DeliveryList = () => {
  const isMobile = useIsMobile();
  return (
    <IthnkPage title="resources.deliveries.name">
      <List
        title={isMobile ? false : undefined}
        disableBreadcrumb={isMobile}
        sort={{ field: "due_date", order: "ASC" }}
        actions={<DeliveryListActions />}
      >
        <PhaseTabs />
        <DeliveryCards />
      </List>
    </IthnkPage>
  );
};

const DeliveryInputs = () => (
  <>
    <TextInput source="title" validate={required()} />
    <ReferenceInput source="company_id" reference="companies">
      <AutocompleteInput />
    </ReferenceInput>
    <ReferenceInput source="contact_id" reference="contacts_summary">
      <AutocompleteInput
        label="resources.deliveries.fields.contact_id"
        optionText={contactOptionText}
      />
    </ReferenceInput>
    <ReferenceInput source="deal_id" reference="deals">
      <AutocompleteInput />
    </ReferenceInput>
    <TextInput source="address" multiline />
    <DateInput source="due_date" />
    <DateTimeInput source="delivered_at" />
    <ReferenceInput source="assigned_sales_id" reference="sales">
      <AutocompleteInput />
    </ReferenceInput>
    <TextInput source="notes" multiline />
  </>
);

export const DeliveryCreate = () => {
  const { identity } = useGetIdentity();
  return (
    <IthnkPage title="resources.deliveries.name">
      <Create redirect="show">
        <SimpleForm defaultValues={{ assigned_sales_id: identity?.id }}>
          <DeliveryInputs />
        </SimpleForm>
      </Create>
    </IthnkPage>
  );
};

export const DeliveryEdit = () => (
  <IthnkPage title="resources.deliveries.name">
    <Edit redirect="show">
      <SimpleForm>
        <DeliveryInputs />
      </SimpleForm>
    </Edit>
  </IthnkPage>
);

/** One column per milestone; drag the delivery to move it, or an invoice to attach it to a milestone */
const MilestoneBoard = ({
  milestones,
  canEdit,
}: {
  milestones: Milestone[];
  canEdit: boolean;
}) => {
  const record = useRecordContext<Delivery>();
  const translate = useTranslate();
  const [update] = useUpdate();
  const { data: invoices = [] } = useGetList<Invoice>(
    "invoices",
    {
      filter: { deal_id: record?.deal_id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: record?.deal_id != null },
  );
  if (!record || !milestones.length) return null;
  const columnOf = (invoice: Invoice) =>
    String(
      milestones.find((m) => m.id === invoice.milestone_id)?.position ?? "none",
    );
  const columns = milestones.map((m) => ({
    id: String(m.position),
    label: m.name,
  }));
  if (invoices.some((invoice) => columnOf(invoice) === "none")) {
    columns.push({
      id: "none",
      label: translate("resources.invoices.no_milestone"),
    });
  }
  const onMove = (itemId: string, columnId: string) => {
    const milestone = milestones.find((m) => String(m.position) === columnId);
    if (itemId === "delivery") {
      if (!milestone) return;
      update(
        "deliveries",
        {
          id: record.id,
          data: {
            current_position: milestone.position,
            phase: deliveryPhase(milestone.position, milestones.length),
          },
          previousData: record,
        },
        { mutationMode: "optimistic" },
      );
      return;
    }
    const invoice = invoices.find((i) => `invoice-${i.id}` === itemId);
    if (!invoice) return;
    update(
      "invoices",
      {
        id: invoice.id,
        data: { milestone_id: milestone?.id ?? null },
        previousData: invoice,
      },
      { mutationMode: "optimistic" },
    );
  };
  return (
    <StatusBoard
      disabled={!canEdit}
      columns={columns}
      onMove={onMove}
      items={[
        {
          id: "delivery",
          column: String(record.current_position),
          content: <DeliveryCard />,
        },
        ...invoices.map((invoice) => ({
          id: `invoice-${invoice.id}`,
          column: columnOf(invoice),
          content: (
            <RecordContextProvider value={invoice}>
              <InvoiceCard />
            </RecordContextProvider>
          ),
        })),
      ]}
    />
  );
};

type MilestoneDraft = { key: string; id?: Identifier; name: string };

/** Add, rename, reorder and remove the milestones between the fixed first and last one */
const MilestonesEditor = ({ milestones }: { milestones: Milestone[] }) => {
  const record = useRecordContext<Delivery>();
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [drafts, setDrafts] = useState<MilestoneDraft[]>([]);
  useEffect(() => {
    setDrafts(
      milestones.map(({ id, name }) => ({ key: String(id), id, name })),
    );
  }, [milestones]);
  if (!record || drafts.length < 2) return null;

  const last = drafts.length - 1;
  const move = (index: number, delta: number) => {
    const next = [...drafts];
    [next[index], next[index + delta]] = [next[index + delta], next[index]];
    setDrafts(next);
  };
  const save = () =>
    dataProvider
      .setDeliveryMilestones(
        record.id,
        drafts.map(({ id, name }) => (id ? { id, name } : { name })),
      )
      .then(refresh)
      .catch((error: Error) => notify(error.message, { type: "error" }));

  return (
    <div className="flex flex-col gap-2 max-w-xl">
      <h3 className="text-base font-medium">
        {translate("resources.deliveries.milestones")}
      </h3>
      {drafts.map((draft, index) => {
        const fixed = index === 0 || index === last;
        return (
          <div key={draft.key} className="flex items-center gap-1">
            <Input
              value={draft.name}
              disabled={fixed}
              onChange={(event) =>
                setDrafts(
                  drafts.map((d, i) =>
                    i === index ? { ...d, name: event.target.value } : d,
                  ),
                )
              }
            />
            <Button
              variant="ghost"
              size="icon"
              disabled={fixed || index === 1}
              onClick={() => move(index, -1)}
            >
              <ArrowUp />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={fixed || index === last - 1}
              onClick={() => move(index, 1)}
            >
              <ArrowDown />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              disabled={fixed}
              onClick={() => setDrafts(drafts.filter((_, i) => i !== index))}
            >
              <Trash2 />
            </Button>
          </div>
        );
      })}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() =>
            setDrafts([
              ...drafts.slice(0, last),
              { key: crypto.randomUUID(), name: "" },
              drafts[last],
            ])
          }
        >
          <Plus />
          {translate("resources.deliveries.add_milestone")}
        </Button>
        <Button
          onClick={save}
          disabled={drafts.some((draft) => !draft.name.trim())}
        >
          {translate("resources.deliveries.save_milestones")}
        </Button>
      </div>
    </div>
  );
};

const DeliveryShowContent = () => {
  const record = useRecordContext<Delivery>();
  const { canAccess: canEdit = false } = useCanAccess({
    resource: "deliveries",
    action: "edit",
  });
  const { data: milestones = [] } = useGetList<Milestone>(
    "delivery_milestones",
    {
      filter: { delivery_id: record?.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "position", order: "ASC" },
    },
    { enabled: !!record },
  );
  if (!record) return null;
  return (
    <div className="flex flex-col gap-6">
      <MilestoneBoard milestones={milestones} canEdit={canEdit} />
      {canEdit ? <MilestonesEditor milestones={milestones} /> : null}
      <ContractPanel dealId={record.deal_id} delivery={record} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecordField source="phase">
          <PhaseField />
        </RecordField>
        <RecordField source="due_date">
          <DateField source="due_date" />
        </RecordField>
        <RecordField source="company_id">
          <ReferenceField source="company_id" reference="companies" />
        </RecordField>
        <RecordField source="contact_id">
          <ReferenceField source="contact_id" reference="contacts" />
        </RecordField>
        <RecordField source="deal_id">
          <ReferenceField source="deal_id" reference="deals" link={false}>
            <TextField source="name" />
          </ReferenceField>
        </RecordField>
        <RecordField source="assigned_sales_id">
          <ReferenceField source="assigned_sales_id" reference="sales" />
        </RecordField>
        <RecordField source="address" />
        <RecordField source="delivered_at">
          <DateField source="delivered_at" showTime />
        </RecordField>
        <RecordField
          source="notes"
          className="md:col-span-2 whitespace-pre-line"
        />
      </div>
    </div>
  );
};

export const DeliveryShow = () => (
  <IthnkPage title="resources.deliveries.name">
    <Show
      actions={
        <CanAccess action="edit">
          <EditButton />
        </CanAccess>
      }
    >
      <DeliveryShowContent />
    </Show>
  </IthnkPage>
);
